import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import AdminDashboardLayout from '../../components/layout/AdminDashboardLayout';
import DataTable from '../../components/common/DataTable';
import usePageTitle from '../../hooks/usePageTitle';
import useSmartRefresh from '../../hooks/useSmartRefresh';
import userManagementService from '../../services/userManagementService';
import walletService from '../../services/walletService';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

/**
 * User Management Page - Manage both public clients and admin users
 */
const UserManagementPage = () => {
  // Set page title
  usePageTitle('User Management');

  const [activeTab, setActiveTab] = useState('clients');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [clientUsers, setClientUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [creditData, setCreditData] = useState({
    amount: '',
    description: 'Manual credit addition',
  });

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'US',
    password: '',
    sendEmail: true,
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch users data
  const fetchUsersData = async () => {
    setIsLoading(true);
    try {
      // Fetch both clients and admins
      const [clientsResponse, adminsResponse] = await Promise.all([
        userManagementService.getAllUsers(),
        userManagementService.getAllAdmins(),
      ]);

      if (clientsResponse.success) {
        setClientUsers(clientsResponse.users || []);
      }

      if (adminsResponse.success) {
        setAdminUsers(adminsResponse.admins || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchUsersData();
  }, []);

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchUsersData,
    'Users refreshed successfully',
  );

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      country: 'US',
      password: '',
      sendEmail: true,
    });
    setFormErrors({});
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!isEditModalOpen && !formData.password) {
      // Password optional for edit, but can be provided to change it
      // For create, we'll let backend generate if not provided
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const tabs = [
    { id: 'clients', label: 'Public Clients', count: clientUsers.length },
    { id: 'admins', label: 'Admin Users', count: adminUsers.length },
  ];

  // Define table columns for clients
  const clientColumns = [
    {
      id: 'name',
      label: 'User',
      sortable: true,
      width: '20%',
      render: (value, item) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
              {value
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {value}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {item.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      sortable: true,
      width: '15%',
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => {
        const statusConfig = {
          active: {
            bg: 'bg-success-100 dark:bg-success-900/30',
            text: 'text-success-700 dark:text-success-400',
            icon: '●',
          },
          inactive: {
            bg: 'bg-gray-100 dark:bg-gray-800',
            text: 'text-gray-700 dark:text-gray-400',
            icon: '●',
          },
          suspended: {
            bg: 'bg-error-100 dark:bg-error-900/30',
            text: 'text-error-700 dark:text-error-400',
            icon: '●',
          },
        };

        const config = statusConfig[value] || statusConfig.inactive;

        return (
          <span
            className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            <span>{config.icon}</span>
            <span className="capitalize">{value}</span>
          </span>
        );
      },
    },
    {
      id: 'totalOrders',
      label: 'Orders',
      sortable: true,
      align: 'center',
      width: '10%',
      render: (value) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">
          {value}
        </span>
      ),
    },
    {
      id: 'totalSpent',
      label: 'Total Spent',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          ${(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      id: 'joinDate',
      label: 'Join Date',
      sortable: true,
      width: '12%',
      render: (value) => (
        <div className="text-gray-900 dark:text-gray-100">{value}</div>
      ),
    },
    {
      id: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      width: '19%',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">{value}</div>
      ),
    },
  ];

  // Define table columns for admins
  const adminColumns = [
    {
      id: 'name',
      label: 'Admin User',
      sortable: true,
      width: '25%',
      render: (value, item) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-red-600 dark:text-red-400 font-medium text-sm">
              {value
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </span>
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {value}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {item.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'role',
      label: 'Role',
      sortable: true,
      width: '20%',
      render: (value) => {
        const roleConfig = {
          super_admin: {
            bg: 'bg-purple-100 dark:bg-purple-900/30',
            text: 'text-purple-700 dark:text-purple-400',
            label: 'Super Admin',
          },
          support_admin: {
            bg: 'bg-blue-100 dark:bg-blue-900/30',
            text: 'text-blue-700 dark:text-blue-400',
            label: 'Support Admin',
          },
          finance_admin: {
            bg: 'bg-emerald-100 dark:bg-emerald-900/30',
            text: 'text-emerald-700 dark:text-emerald-400',
            label: 'Finance Admin',
          },
        };

        const config = roleConfig[value] || roleConfig.support_admin;

        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            {config.label}
          </span>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '15%',
      render: (value) => {
        const statusConfig = {
          active: {
            bg: 'bg-success-100 dark:bg-success-900/30',
            text: 'text-success-700 dark:text-success-400',
            icon: '●',
          },
          inactive: {
            bg: 'bg-gray-100 dark:bg-gray-800',
            text: 'text-gray-700 dark:text-gray-400',
            icon: '●',
          },
        };

        const config = statusConfig[value] || statusConfig.inactive;

        return (
          <span
            className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
          >
            <span>{config.icon}</span>
            <span className="capitalize">{value}</span>
          </span>
        );
      },
    },
    {
      id: 'joinDate',
      label: 'Join Date',
      sortable: true,
      width: '15%',
      render: (value) => (
        <div className="text-gray-900 dark:text-gray-100">{value}</div>
      ),
    },
    {
      id: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      width: '25%',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">{value}</div>
      ),
    },
  ];

  // Define table actions
  const actions = [
    {
      type: 'view',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
      tooltip: 'View Details',
      variant: 'primary',
    },
    {
      type: 'credit',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      tooltip: 'Add Credit',
      variant: 'success',
      hidden: (item) => activeTab !== 'clients',
    },
    {
      type: 'edit',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
          />
        </svg>
      ),
      tooltip: 'Edit User',
    },
    {
      type: 'delete',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
          />
        </svg>
      ),
      tooltip: 'Delete User',
      variant: 'danger',
    },
  ];

  // Define filters
  const clientFilters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'suspended', label: 'Suspended' },
      ],
    },
  ];

  const adminFilters = [
    {
      key: 'role',
      label: 'Role',
      options: [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'support_admin', label: 'Support Admin' },
        { value: 'finance_admin', label: 'Finance Admin' },
      ],
    },
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    },
  ];

  // Action handlers
  const handleView = (user) => {
    setSelectedUser(user);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      country: user.country || 'US',
      password: '',
      sendEmail: true,
    });
    setIsEditModalOpen(true);
  };

  const handleCredit = (user) => {
    console.log('handleCredit called for user:', user);
    setSelectedUser(user);
    setCreditData({
      amount: '',
      description: 'Manual credit addition',
    });
    setIsCreditModalOpen(true);
  };

  const handleDelete = async (user) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${user.name}? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      const response =
        activeTab === 'clients'
          ? await userManagementService.deleteUser(user.id)
          : await userManagementService.deleteAdmin(user.id);

      if (response.success) {
        toast.success(
          response.message ||
            `${activeTab === 'clients' ? 'User' : 'Admin'} deleted successfully`,
        );
        await fetchUsersData();
      } else {
        toast.error(response.message || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      const errorMessage =
        error.message || 'Failed to delete. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleCreateUser = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response =
        activeTab === 'clients'
          ? await userManagementService.createUser(formData)
          : await userManagementService.createAdmin(formData);

      if (response.success) {
        toast.success(
          response.message ||
            `${activeTab === 'clients' ? 'User' : 'Admin'} created successfully`,
        );
        setIsCreateModalOpen(false);
        resetForm();
        await fetchUsersData();
      } else {
        // Handle validation errors from backend
        if (response.errors) {
          setFormErrors(response.errors);
        }
        toast.error(response.message || 'Failed to create');
      }
    } catch (error) {
      console.error('Error creating:', error);
      const errorMessage =
        error.message || 'Failed to create. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
      };

      const response =
        activeTab === 'clients'
          ? await userManagementService.updateUser(selectedUser.id, updateData)
          : await userManagementService.updateAdmin(
              selectedUser.id,
              updateData,
            );

      if (response.success) {
        toast.success(
          response.message ||
            `${activeTab === 'clients' ? 'User' : 'Admin'} updated successfully`,
        );
        setIsEditModalOpen(false);
        setSelectedUser(null);
        resetForm();
        await fetchUsersData();
      } else {
        // Handle validation errors from backend
        if (response.errors) {
          setFormErrors(response.errors);
        }
        toast.error(response.message || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating:', error);
      const errorMessage =
        error.message || 'Failed to update. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleSubmitCredit = async (e) => {
    e.preventDefault();

    if (!creditData.amount || isNaN(creditData.amount)) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setIsLoading(true);
      const response = await walletService.adjustCredit(
        selectedUser.id,
        creditData.amount,
        creditData.description,
      );

      if (response.success) {
        toast.success(response.message || 'Credit adjusted successfully');
        setIsCreditModalOpen(false);
        setCreditData({ amount: '', description: 'Manual credit addition' });
        await fetchUsersData();
      } else {
        toast.error(response.message || 'Failed to adjust credit');
      }
    } catch (error) {
      console.error('Error adjusting credit:', error);
      toast.error(error.message || 'Failed to adjust credit');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminDashboardLayout pageName="User Management">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Manage public clients and admin users with full CRUD operations
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="border-b border-gray-200 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Create User Button */}
        <motion.div
          className="flex justify-end"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <button
            onClick={handleCreateUser}
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 text-white rounded-lg font-medium transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            Create {activeTab === 'clients' ? 'Client' : 'Admin'}
          </button>
        </motion.div>

        {/* DataTable Component */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DataTable
              title={`${activeTab === 'clients' ? 'Public Clients' : 'Admin Users'} Management`}
              columns={activeTab === 'clients' ? clientColumns : adminColumns}
              data={activeTab === 'clients' ? clientUsers : adminUsers}
              actions={actions}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCredit={handleCredit}
              onRefresh={refreshData}
              searchPlaceholder={`Search ${activeTab === 'clients' ? 'clients' : 'admins'}...`}
              filters={activeTab === 'clients' ? clientFilters : adminFilters}
              rowsPerPageOptions={[5, 10, 20, 50]}
              defaultRowsPerPage={10}
              exportEnabled={true}
              refreshEnabled={true}
              isLoading={isRefreshing || isLoading}
              className="mt-6"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Create User/Admin Modal */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetForm();
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">
                      Create New {activeTab === 'clients' ? 'Client' : 'Admin'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Fill in the details to create a new{' '}
                      {activeTab === 'clients' ? 'client user' : 'admin user'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      resetForm();
                    }}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmitCreate} className="p-6 space-y-4">
                  <Input
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={formErrors.name}
                    placeholder="Enter full name"
                    required
                  />

                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={formErrors.email}
                    placeholder="Enter email address"
                    required
                  />

                  <Input
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={formErrors.phone}
                    placeholder="Enter phone number (optional)"
                  />

                  {activeTab === 'clients' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Country
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      >
                        <option value="US">United States</option>
                        <option value="UK">United Kingdom</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="IN">India</option>
                        <option value="JP">Japan</option>
                      </select>
                    </div>
                  )}

                  <Input
                    label="Password (Optional)"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={formErrors.password}
                    placeholder="Leave blank to auto-generate"
                    helperText="If left blank, a secure password will be generated and sent via email"
                  />

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="sendEmail"
                      checked={formData.sendEmail}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Send welcome email with login credentials
                    </label>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsCreateModalOpen(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                      Create {activeTab === 'clients' ? 'User' : 'Admin'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User/Admin Modal */}
      <AnimatePresence>
        {isEditModalOpen && selectedUser && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedUser(null);
                resetForm();
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">
                      Edit {activeTab === 'clients' ? 'Client' : 'Admin'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Update {activeTab === 'clients' ? 'client' : 'admin'}{' '}
                      information
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedUser(null);
                      resetForm();
                    }}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
                  <Input
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={formErrors.name}
                    placeholder="Enter full name"
                    required
                  />

                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={formErrors.email}
                    placeholder="Enter email address"
                    required
                  />

                  <Input
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={formErrors.phone}
                    placeholder="Enter phone number (optional)"
                  />

                  {activeTab === 'clients' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Country
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      >
                        <option value="US">United States</option>
                        <option value="UK">United Kingdom</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="DE">Germany</option>
                        <option value="FR">France</option>
                        <option value="IN">India</option>
                        <option value="JP">Japan</option>
                      </select>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEditModalOpen(false);
                        setSelectedUser(null);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                      Update {activeTab === 'clients' ? 'User' : 'Admin'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User View Modal */}
      <AnimatePresence>
        {selectedUser && !isEditModalOpen && !isCreditModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => setSelectedUser(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">
                      User Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {selectedUser.id}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Basic Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Name
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedUser.name}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Email
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedUser.email}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Phone
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedUser.phone}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Status
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              selectedUser.status === 'active'
                                ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                : selectedUser.status === 'suspended'
                                  ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {selectedUser.status.charAt(0).toUpperCase() +
                              selectedUser.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        {activeTab === 'clients'
                          ? 'Account Details'
                          : 'Admin Details'}
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Join Date
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedUser.joinDate}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Last Login
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {selectedUser.lastLogin}
                          </p>
                        </div>
                        {activeTab === 'clients' ? (
                          <>
                            <div>
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                Total Orders
                              </p>
                              <p className="text-sm text-gray-900 dark:text-gray-100">
                                {selectedUser.totalOrders}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                Total Spent
                              </p>
                              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                ${selectedUser.totalSpent?.toFixed(2)}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                Role
                              </p>
                              <p className="text-sm text-gray-900 dark:text-gray-100">
                                {selectedUser.role
                                  ?.replace('_', ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                Permissions
                              </p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {selectedUser.permissions?.map(
                                  (permission, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                    >
                                      {permission}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                  <button
                    onClick={() => handleEdit(selectedUser)}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Edit User
                  </button>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Credit Modal */}
      <AnimatePresence>
        {isCreditModalOpen && selectedUser && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => {
                setIsCreditModalOpen(false);
                setSelectedUser(null);
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
                      Add User Credit
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      For: {selectedUser.name} ({selectedUser.email})
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsCreditModalOpen(false);
                      setSelectedUser(null);
                    }}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmitCredit} className="p-6 space-y-4">
                  <Input
                    label="Credit Amount ($)"
                    name="amount"
                    type="number"
                    step="0.01"
                    value={creditData.amount}
                    onChange={(e) =>
                      setCreditData({ ...creditData, amount: e.target.value })
                    }
                    placeholder="e.g. 5.00"
                    required
                  />

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={creditData.description}
                      onChange={(e) =>
                        setCreditData({
                          ...creditData,
                          description: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 transition-colors"
                      rows="3"
                      placeholder="Reason for adding credit..."
                      required
                    ></textarea>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsCreditModalOpen(false);
                        setSelectedUser(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="bg-emerald-600 hover:bg-emerald-700 border-none"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Processing...' : 'Confirm Addition'}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminDashboardLayout>
  );
};

export default UserManagementPage;
